/**
 * Safe in-app path after login/signup. Rejects protocol-relative and external URLs.
 */
export function safeAppRedirectPath(raw: string | null): string | null {
  if (raw == null || typeof raw !== 'string') return null
  const t = raw.trim()
  if (!t.startsWith('/') || t.startsWith('//')) return null
  return t
}

const MCP_OAUTH_STORAGE_KEY = 'flownotes_mcp_oauth_pending'

export type McpOAuthPendingParams = {
  client_id: string
  redirect_uri: string
  state: string
  code_challenge: string
  code_challenge_method: string
  scope: string
}

export function saveMcpOAuthPendingParams(params: McpOAuthPendingParams): void {
  try {
    sessionStorage.setItem(MCP_OAUTH_STORAGE_KEY, JSON.stringify(params))
  } catch {
    /* ignore quota / private mode */
  }
}

export function loadMcpOAuthPendingParams(): McpOAuthPendingParams | null {
  try {
    const raw = sessionStorage.getItem(MCP_OAUTH_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<McpOAuthPendingParams>
    if (
      !parsed.client_id ||
      !parsed.redirect_uri ||
      !parsed.state ||
      !parsed.code_challenge ||
      !parsed.code_challenge_method
    ) {
      return null
    }
    return {
      client_id: parsed.client_id,
      redirect_uri: parsed.redirect_uri,
      state: parsed.state,
      code_challenge: parsed.code_challenge,
      code_challenge_method: parsed.code_challenge_method,
      scope: typeof parsed.scope === 'string' ? parsed.scope : '',
    }
  } catch {
    return null
  }
}

export function clearMcpOAuthPendingParams(): void {
  try {
    sessionStorage.removeItem(MCP_OAUTH_STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

/** React Router location.state.from fallback for login redirect. */
export function redirectPathFromLoginState(state: unknown): string | null {
  if (!state || typeof state !== 'object' || !('from' in state)) return null
  const from = (state as { from?: unknown }).from
  if (!from || typeof from !== 'object' || !('pathname' in from)) return null
  const pathname = (from as { pathname?: unknown }).pathname
  if (typeof pathname !== 'string') return null
  const search = (from as { search?: unknown }).search
  return safeAppRedirectPath(
    `${pathname}${typeof search === 'string' ? search : ''}`
  )
}
